import { z } from "zod";

export const LOADING_PHRASES_WINDOW = 3;

export const loadingPhrasesFileSchema = z.object({
  chapters: z.array(z.object({
    chapterId: z.string().min(1),
    phrases: z.array(z.string().min(1)),
  })),
});

export type LoadingPhrasesFile = z.infer<typeof loadingPhrasesFileSchema>;

function chapterNumber(chapterId: string): number {
  return Number(/\d+/.exec(chapterId)?.[0] ?? 0);
}

/**
 * The viewer's chapter and the two before it, so a phrase never spoils a later one.
 * No known chapter means a first visit, which gets the opening chapter only.
 */
export function phrasesForChapter(
  file: LoadingPhrasesFile,
  chapterId: string | null,
): string[] {
  const ordered = [...file.chapters].sort(
    (a, b) => chapterNumber(a.chapterId) - chapterNumber(b.chapterId),
  );
  const current = chapterId === null
    ? chapterNumber(ordered[0]?.chapterId ?? "")
    : chapterNumber(chapterId);
  return ordered
    .filter((chapter) => {
      const number = chapterNumber(chapter.chapterId);
      return number <= current && number > current - LOADING_PHRASES_WINDOW;
    })
    .flatMap((chapter) => chapter.phrases);
}

/** A random phrase that is never the one on screen, unless it is the only one. */
export function nextLoadingPhrase(
  phrases: readonly string[],
  current: string | null,
  random: () => number = Math.random,
): string | null {
  const choices = phrases.filter((phrase) => phrase !== current);
  if (choices.length === 0) {
    return phrases[0] ?? null;
  }
  return choices[Math.floor(random() * choices.length)] ?? null;
}
